import { IAppTermShallowDocument } from '@services/legals';

/**
 * AppKeyFeaturesMask provides the order and mapping for the featuresMask
 * bitmask. Use bitmask-utility.services.ts for working with these values
 * and IAppKeyFeaturesMask for Object creation from the mapping.
 */
export const AppKeyFeaturesMask = [
	'denyLoginEmailUnverified',
	'denyLoginUnacceptedTerms',
	'encryptJwt',
	'laxPasswordComplexity',
	'haveIBeenPwnedPasswordComplexity',
	'autoAcceptTerms',
	'laxLoginSecret',
	'z5JWT',
	'refreshRequiresTerms',
	'tpNoDecorate',
	'noNullNames',
	'invalidateRefreshTokens',
	'loginMfaEnabled',
	'loginMfaEnforced'
];

export interface IAppKeyFeaturesMask {
	denyLoginEmailUnverified: boolean;
	denyLoginUnacceptedTerms: boolean;
	encryptJwt: boolean;
	laxPasswordComplexity: boolean;
	haveIBeenPwnedPasswordComplexity: boolean;
	autoAcceptTerms: boolean;
	laxLoginSecret: boolean;
	z5JWT: boolean;
	refreshRequiresTerms: boolean;
	tpNoDecorate: boolean;
	noNullNames: boolean;
	invalidateRefreshTokens: boolean;
	loginMfaEnabled: boolean;
	loginMfaEnforced: boolean;
}

export enum AppKeyProvider {
	cognito = 'cognito',
	gigya = 'gigya',
	local = 'local',
	entra = 'entra'
}

export interface IAppKey {
	name: string;
	clientId: string;
	clientSecret: string;
	clusterId: string;
	clusterName: string;
	scope: number;
	refreshTokenValidityDays: number;
	accessTokenExpiration: number;
	refreshTokenExpiration: number;
	valid: boolean;
	redirectUri?: string;
	provider: AppKeyProvider;
	emailVerifyUrl: string;
	passwordResetUrl: string;
	sigSecret: string;
	featuresMask: number;
	requiredTerms?: IAppTermShallowDocument[];
}

export interface IAppKeyDisplay {
	clientId: string;
	name: string;
	provider?: string;
}

export interface IAppKeyWithOptionals {
	name: string;
	companyId: string;
	clientId?: string;
	clusterId?: string;
	accessTokenExpiration: number;
	refreshTokenExpiration: number;
	provider?: string;
}

export interface IAppKeyUpdate {
	accessTokenExpiration?: number;
	emailVerifyUrl?: string;
	featuresMask?: number;
	name?: string;
	passwordResetUrl?: string;
	refreshTokenExpiration?: number;
	sigSecret?: string;
	valid?: boolean;
}

export interface AppCluster {
	clusterId: string;
	name: string;
	region: string;
	hostnames: string[];
	companyId: string;
	featuresMask: number;
}

export interface IAppClusterDisplay {
	clusterId: string;
	name: string;
}

export interface IAppClusterSelection {
	selectedCluster: AppCluster;
	clusters: AppCluster[];
}

export enum RequiredAppVersionAction {
	forcedImmediately = 'FORCED_IMMEDIATELY',
	forcedNextConvenient = 'FORCED_NEXT_CONVENIENT',
	recommended = 'RECOMMENDED'
}

export interface IRequiredAppVersion {
	action: RequiredAppVersionAction;
	version: string;
	buildNumber: number;
	minOSVersion: string;
	releasedAt: number;
}

export type IRequiredAppVersionMap = Record<RequiredAppVersionAction, IRequiredAppVersion>;
export type IRequiredAppVersions = Record<string, IRequiredAppVersionMap>;

export interface IAppKeyServiceData {
	appKeys: { [clientId: string]: IAppKey };
	clusters: {
		all: AppCluster[];
	};
}
